import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { ShieldCheckIcon, CheckIcon, ChevronDownIcon } from 'lucide-react';
import { useFormState } from '../hooks/useFormState';
import { degreeInterestOptions, focusOptions, specializationOptions, getSpecializationsForFocus } from '../data/degrees';
import { hasMatchParams, readMatchParams } from '../utils/matchParams';
interface HeroSectionProps {
  onSelectionChange?: (degreeInterest: string, areaOfStudy: string, specialization: string) => void;
}
export function HeroSection({
  onSelectionChange
}: HeroSectionProps) {
  // Clean-URL form: /degree/:areaStudy/:program/:degreeInterest — the third
  // segment is optional.
  const {
    areaStudy,
    program,
    degreeInterest: degreeInterestParam
  } = useParams<{
    areaStudy?: string;
    program?: string;
    degreeInterest?: string;
  }>();
  // Anchor links elsewhere on the site carry the visitor's context in the URL
  // (?degreeInterest=&areaStudy=&mProgram=), so the widget opens pre-filled.
  const urlPrefill = readMatchParams();
  const {
    formData,
    errors,
    updateFormData,
    updateMultiple,
    validateHeroForm
  } = useFormState(hasMatchParams(urlPrefill) ? urlPrefill : undefined);
  useEffect(() => {
    if (areaStudy && program && !formData.areaOfStudy) {
      updateMultiple({
        areaOfStudy: areaStudy,
        specialization: program,
        // A query string still wins, so ?degreeInterest= can override the path.
        ...(degreeInterestParam && !formData.degreeInterest ? {
          degreeInterest: degreeInterestParam
        } : {})
      });
    }
  }, [areaStudy, program, degreeInterestParam, formData.areaOfStudy, formData.degreeInterest, updateMultiple]);
  const filteredSpecializations = formData.areaOfStudy ? getSpecializationsForFocus(formData.areaOfStudy) : [];
  const handleDegreeInterestChange = (value: string) => {
    updateFormData('degreeInterest', value);
    if (onSelectionChange) {
      onSelectionChange(value, formData.areaOfStudy, formData.specialization);
    }
  };
  const handleAreaOfStudyChange = (value: string) => {
    updateFormData('areaOfStudy', value);
    updateFormData('specialization', '');
    if (onSelectionChange) {
      onSelectionChange(formData.degreeInterest, value, '');
    }
  };
  const handleSpecializationChange = (value: string) => {
    updateFormData('specialization', value);
    if (onSelectionChange) {
      onSelectionChange(formData.degreeInterest, formData.areaOfStudy, value);
    }
  };
  const handleContinue = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateHeroForm() && onSelectionChange) {
      onSelectionChange(formData.degreeInterest, formData.areaOfStudy, formData.specialization);
    }
  };
  // Resolve display labels for the headline
  const selectedFocus = focusOptions.find(f => f.value === formData.areaOfStudy);
  const selectedSpecOption = specializationOptions.find(s => s.value === formData.specialization);
  const selectedSpecName = selectedSpecOption?.type;
  // --- H1 headline logic ---
  const currentYear = new Date().getFullYear();
  const searchParams = new URLSearchParams(window.location.search);
  const t10verride = searchParams.get('t10verride');
  const smallWords = new Set(['for', 'in', 'of', 'a', 'an', 'the', 'and', '&']);
  function capitalizeHeadline(text: string): string {
    return text.split(' ').map((word, idx) => {
      if (idx === 0) return word.charAt(0).toUpperCase() + word.slice(1);
      if (smallWords.has(word.toLowerCase())) return word.toLowerCase();
      return word.charAt(0).toUpperCase() + word.slice(1);
    }).join(' ');
  }
  const degreeNameMap: Record<string, string> = {
    '1': 'Certificate',
    '2': "Associate's",
    '3': "Bachelor's",
    '4': "Master's"
  };
  const hasSpecificDegree = ['1', '2', '3', '4'].includes(formData.degreeInterest);
  const degreeName = degreeNameMap[formData.degreeInterest] || '';
  const isGeneralArea = formData.areaOfStudy === '9';
  const isGeneralProgram = !selectedSpecName || /^(General|All )/i.test(selectedSpecName);
  const areaName = selectedFocus?.type || '';
  function buildHeadline(): string {
    if (hasSpecificDegree) {
      if (!isGeneralProgram && selectedSpecName) {
        // Case 1: Specific degree + specific program
        return `Best Online ${degreeName} Degree in ${selectedSpecName} of ${currentYear}`;
      }
      if (isGeneralArea) {
        // Case 2: Specific degree + General area
        return `Best Colleges for a ${degreeName} Degree Program in ${currentYear}`;
      }
      // Case 3: Specific degree + specific area but general/all program
      return `Best Online ${degreeName} Degree in ${areaName} of ${currentYear}`;
    }
    // No specific degree selected
    if (isGeneralArea || !formData.areaOfStudy && !formData.specialization) {
      // Case 4a: default landing
      return `Top Accredited Degree Programs of ${currentYear}`;
    }
    if (isGeneralProgram || !selectedSpecName) {
      // Case 4b: No degree + specific area or "General" program
      return `Best Colleges for an Online ${areaName} Degree of ${currentYear}`;
    }
    // Case 4c: No degree + specific program
    return `Best Colleges for an Online ${selectedSpecName} Degree of ${currentYear}`;
  }
  let headline = capitalizeHeadline(buildHeadline());
  // Apply t10verride if present
  if (t10verride) {
    headline = headline.replace(/^Top Accredited/i, t10verride).replace(/^Best Online/i, t10verride).replace(/^Best Colleges for/i, t10verride);
  }
  const heroBenefits = ['Personalized degree program matches', 'Support from choosing a school through graduation', 'Financial aid insights for your degree'];
  const selectClass = (hasError: boolean) => `block w-full h-12 pl-4 pr-10 text-sm font-medium text-ink rounded-xl bg-white border appearance-none cursor-pointer outline-none transition-colors focus:ring-2 focus:ring-offset-2 focus:ring-offset-secondary disabled:bg-neutral-100 disabled:text-ink-muted disabled:cursor-not-allowed ${hasError ? 'border-error focus:ring-error' : 'border-transparent focus:ring-white'}`;
  return <section className="relative bg-background-alt overflow-hidden" data-id="element-420" data-mp="1p4gkh0zm3881">
      {/* The brand photograph sits far back as atmosphere so the headline and
          the match card stay the only things competing for attention. */}
      <img src="https://cdn.magicpatterns.com/uploads/mUaxjLxnPwBJa8FwQnbTuQ/Hero_Background_FOCC.png" alt="" aria-hidden="true" className="pointer-events-none absolute inset-0 h-full w-full object-cover object-bottom -scale-x-100 opacity-25" data-id="element-421" data-mp="whdmqb2rf1iu" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-12 md:py-16 lg:py-20" data-id="element-422" data-mp="8qgn9xs5hwoh">
        <div className="grid gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:gap-14 lg:items-center" data-id="element-423" data-mp="dpb6v1xxzp9n">
          {/* Copy + what you get */}
          <div className="text-center lg:text-left" data-id="element-424" data-mp="64r2nydyxt5m">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#EDF7E7] ring-1 ring-inset ring-[#C9E6B8] text-sm font-semibold mb-5 text-[#3F7F22]" data-id="element-425" data-mp="17h5txo92f7r">
              <span className="flex h-2 w-2 rounded-full bg-accent" data-id="element-426" data-mp="3nf7ak5h4pgfx" />
              Fall 2026 Enrollment Open
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-[3.25rem] font-extrabold text-ink tracking-tight mb-5 leading-[1.12]" data-id="element-427" data-mp="3jdlte7xkafzx">
              {headline}
            </h1>

            <p className="text-base md:text-lg text-ink-secondary mb-8 max-w-xl mx-auto lg:mx-0 leading-relaxed" data-id="element-428" data-mp="39n0tmh65j5ff">
              Compare accredited online colleges and universities. Get matched
              with programs that fit your career goals, schedule, and budget.
            </p>

            <ul className="space-y-4 text-left max-w-xl mx-auto lg:mx-0" data-id="element-429" data-mp="2vo15damjwsjs">
              {heroBenefits.map(benefit => <li key={benefit} className="flex items-start gap-3" data-id="element-430" data-mp="netd353xl347">
                  <span aria-hidden="true" className="mt-0.5 inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-primary text-white" data-id="element-431" data-mp="se613t6jgg8d">
                    <CheckIcon className="h-3.5 w-3.5" strokeWidth={3} data-id="element-432" data-mp="ifgn4bhrjfxe" />
                  </span>
                  <span className="text-base font-medium text-ink" data-id="element-433" data-mp="4o1udcy200d5">
                    {benefit}
                  </span>
                </li>)}
            </ul>

            <p className="mt-8 flex items-center justify-center lg:justify-start gap-2 text-sm font-semibold text-ink-secondary" data-id="element-434" data-mp="3qvtfpqf2clkk">
              <ShieldCheckIcon className="w-4 h-4 flex-shrink-0 text-accent" aria-hidden="true" data-id="element-435" data-mp="3vu3cg5gfru8a" />
              Trusted by 2M+ students to find the right school
            </p>
          </div>

          {/* Match card */}
          <div className="w-full max-w-md mx-auto lg:mx-0 lg:justify-self-end" data-id="element-436" data-mp="338epgzaer87b">
            <form onSubmit={handleContinue} aria-labelledby="hero-match-heading" className="rounded-2xl bg-secondary p-6 sm:p-8 shadow-ds-lg" data-id="element-437" data-mp="33airtcsnrz5h">
              <h2 id="hero-match-heading" className="text-2xl font-bold text-white text-center" data-id="element-438" data-mp="3c703ch4xpz1r">
                Get Matched
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-white/80 text-center" data-id="element-439" data-mp="3m5frqcckuu7k">
                Answer three quick questions and we'll show you accredited
                programs that fit.
              </p>

              <div className="mt-6 space-y-3" data-id="element-440" data-mp="3v1f3wb6w9zrp">
                <div data-id="element-441" data-mp="3qz1wzjgevvtc">
                  <label htmlFor="degreeInterest" className="sr-only" data-id="element-442" data-mp="3qyiqcyw7otum">
                    Degree level
                  </label>
                  <div className="relative" data-id="element-443" data-mp="8q1cno6znceb">
                    <select id="degreeInterest" value={formData.degreeInterest} onChange={e => handleDegreeInterestChange(e.target.value)} className={selectClass(Boolean(errors.degreeInterest))} data-id="element-444" data-mp="3uc9zbzo9fds">
                      <option value="" data-id="element-445" data-mp="2d16ulmucg5js">Select degree level</option>
                      {degreeInterestOptions.map(opt => <option key={opt.value} value={opt.value} data-id="element-446" data-mp="39rqyyvww4yby">
                          {opt.type}
                        </option>)}
                    </select>
                    <ChevronDownIcon className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary" aria-hidden="true" data-id="element-447" data-mp="30q9lzmes5crh" />
                  </div>
                  {errors.degreeInterest && <p className="mt-1.5 text-xs font-medium text-white" data-id="element-448" data-mp="1kcsvt4vfl576">
                      {errors.degreeInterest}
                    </p>}
                </div>

                <div data-id="element-449" data-mp="1a6roiuqxol9q">
                  <label htmlFor="areaOfStudy" className="sr-only" data-id="element-450" data-mp="1f75um78z5l54">
                    Area of study
                  </label>
                  <div className="relative" data-id="element-451" data-mp="1k60d183157op">
                    <select id="areaOfStudy" value={formData.areaOfStudy} onChange={e => handleAreaOfStudyChange(e.target.value)} className={selectClass(Boolean(errors.areaOfStudy))} data-id="element-452" data-mp="1k4xp99107ld0">
                      <option value="" data-id="element-453" data-mp="1y001sf0cw8ys">Select area of study</option>
                      {focusOptions.map(opt => <option key={opt.value} value={opt.value} data-id="element-454" data-mp="27w34pn318bms">
                          {opt.type}
                        </option>)}
                    </select>
                    <ChevronDownIcon className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary" aria-hidden="true" data-id="element-455" data-mp="dibscftbjdnn" />
                  </div>
                  {errors.areaOfStudy && <p className="mt-1.5 text-xs font-medium text-white" data-id="element-456" data-mp="kx8polbta5l">
                      {errors.areaOfStudy}
                    </p>}
                </div>

                <div data-id="element-457" data-mp="aitlektwwd5t">
                  <label htmlFor="specialization" className="sr-only" data-id="element-458" data-mp="ogi4bn4f5a88">
                    Program
                  </label>
                  <div className="relative" data-id="element-459" data-mp="ji6w1gtrv7s4">
                    <select id="specialization" value={formData.specialization} onChange={e => handleSpecializationChange(e.target.value)} disabled={!formData.areaOfStudy} className={selectClass(Boolean(errors.specialization))} data-id="element-460" data-mp="jh5b8kmyf6x7">
                      <option value="" data-id="element-461" data-mp="133beyf2ztru3">
                        {formData.areaOfStudy ? 'Select program' : 'Select an area of study first'}
                      </option>
                      {filteredSpecializations.map(opt => <option key={opt.value} value={opt.value} data-id="element-462" data-mp="1csqpf2drpvd6">
                          {opt.type}
                        </option>)}
                    </select>
                    <ChevronDownIcon className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary" aria-hidden="true" data-id="element-463" data-mp="131npi3wkrmfh" />
                  </div>
                  {errors.specialization && <p className="mt-1.5 text-xs font-medium text-white" data-id="element-464" data-mp="y4ngbk4fi3k5">
                      {errors.specialization}
                    </p>}
                </div>
              </div>

              <button type="submit" className="mt-6 w-full inline-flex items-center justify-center gap-2 h-[52px] px-8 text-white font-semibold rounded-full text-base leading-none shadow-ds-md transition-colors duration-150 ease-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white" style={{
              backgroundColor: '#5cb335',
              backgroundImage: 'none'
            }} data-id="element-465" data-mp="p2wcylxd28tg">
                Find My Program
              </button>

              <p className="mt-4 text-xs leading-relaxed text-white/70 text-center" data-id="element-466" data-mp="18o9k3ny17qni">
                Free for students. No obligation to enroll.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>;
}